import { useEffect, useRef } from "react";

const TUBES_CURSOR_MODULE_URL =
  "https://cdn.jsdelivr.net/npm/threejs-components@0.0.19/build/cursors/tubes1.min.js";
const POINTER_FOLLOW_LERP = 0.5;
const LOOP_GLIDE_DURATION = 1400;
const POINTER_GLIDE_DURATION = 1000;

type TargetPoint = { x: number; y: number; z: number };
type FrameTime = { delta: number; elapsed: number };
type PointerSnapshot = {
  buttons: number;
  clientX: number;
  clientY: number;
  isPrimary: boolean;
  pointerId: number;
  pointerType: string;
};

type TubesCursorInstance = {
  dispose?: () => void;
  tubes: {
    options: { lerp: number };
    target: TargetPoint;
    update: (time: FrameTime) => void;
    setColors: (colors: string[]) => void;
    setLightsColors: (colors: string[]) => void;
  };
};

type TubesCursorFactory = (
  canvas: HTMLCanvasElement,
  options: {
    tubes: {
      colors: string[];
      lerp: number;
      lights: { intensity: number; colors: string[] };
    };
  },
) => TubesCursorInstance;

function randomColors(count: number) {
  return Array.from({ length: count }, () =>
    `#${Math.floor(Math.random() * 0xffffff)
      .toString(16)
      .padStart(6, "0")}`,
  );
}

function smootherstep(progress: number) {
  return progress * progress * progress * (progress * (progress * 6 - 15) + 10);
}

function isInsideViewport({ clientX, clientY }: Pick<PointerSnapshot, "clientX" | "clientY">) {
  return clientX > 0 && clientX < window.innerWidth && clientY > 0 && clientY < window.innerHeight;
}

function snapshotPointer(event: PointerEvent): PointerSnapshot {
  return {
    buttons: event.buttons,
    clientX: event.clientX,
    clientY: event.clientY,
    isPrimary: event.isPrimary,
    pointerId: event.pointerId,
    pointerType: event.pointerType,
  };
}

export default function TubesCursor({ active }: { active: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const appRef = useRef<TubesCursorInstance | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !active) return;

    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!finePointer.matches || reducedMotion.matches) return;

    let disposed = false;
    let removeTargetGlide = () => {};
    let lastPointer: PointerSnapshot | null = null;
    let setPointerPresence: (present: boolean) => void = () => {};
    let replayPointer: (pointer: PointerSnapshot) => void = () => {};

    const pointerHitsCanvas = (pointer: PointerSnapshot) => {
      const rect = canvas.getBoundingClientRect();
      return (
        pointer.clientX >= rect.left &&
        pointer.clientX <= rect.right &&
        pointer.clientY >= rect.top &&
        pointer.clientY <= rect.bottom
      );
    };

    const targetBelongsToPage = (target: EventTarget | null) =>
      target instanceof Node && (target === document.body || document.body.contains(target));

    const handlePointerActivity = (event: PointerEvent) => {
      if (!event.isTrusted || !event.isPrimary || event.pointerType !== "mouse") return;

      const pointer = snapshotPointer(event);
      if (pointerHitsCanvas(pointer)) {
        lastPointer = pointer;
        setPointerPresence(true);

        // The cursor package only listens on document.body. Events targeting
        // the root document never reach that listener, so replay just those.
        if (!targetBelongsToPage(event.target)) replayPointer(pointer);
        return;
      }

      // Native scrollbars live inside the viewport but outside the DOM event
      // tree. Preserve the last page position while the pointer is over one.
      if (targetBelongsToPage(event.target)) setPointerPresence(false);
    };

    const handleBodyPointerLeave = (event: PointerEvent) => {
      if (!event.isTrusted) return;

      if (document.visibilityState === "visible" && isInsideViewport(event)) {
        // Entering browser chrome such as the scrollbar fires pointerleave on
        // body even though the pointer has not left the visible tab.
        event.stopImmediatePropagation();
        if (lastPointer && pointerHitsCanvas(lastPointer)) setPointerPresence(true);
        return;
      }

      setPointerPresence(false);
    };

    const sendPointerLeaveToLibrary = () => {
      document.body.dispatchEvent(
        new PointerEvent("pointerleave", {
          clientX: -1,
          clientY: -1,
          pointerId: lastPointer?.pointerId ?? 1,
          pointerType: lastPointer?.pointerType ?? "mouse",
        }),
      );
    };

    const handleWindowBlur = () => {
      setPointerPresence(false);
      sendPointerLeaveToLibrary();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) handleWindowBlur();
    };

    // Register the capture listener before the cursor package initializes so
    // false body-leave events can be stopped before its global listener runs.
    document.body.addEventListener("pointerleave", handleBodyPointerLeave, true);
    window.addEventListener("pointermove", handlePointerActivity, { capture: true, passive: true });
    window.addEventListener("pointerover", handlePointerActivity, { capture: true, passive: true });
    window.addEventListener("pointerdown", handlePointerActivity, { capture: true, passive: true });
    window.addEventListener("blur", handleWindowBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const randomize = () => {
      appRef.current?.tubes.setColors(randomColors(3));
      appRef.current?.tubes.setLightsColors(randomColors(4));
    };

    const hero = canvas.closest<HTMLElement>(".hero");
    hero?.addEventListener("click", randomize);

    const initTimer = window.setTimeout(() => {
      void import(/* @vite-ignore */ TUBES_CURSOR_MODULE_URL)
        .then((module) => {
          if (disposed || !canvasRef.current) return;

          const createTubesCursor = (module as { default: TubesCursorFactory }).default;
          const app = createTubesCursor(canvasRef.current, {
            tubes: {
              colors: ["#23d6ff", "#785cff", "#ff3d9a"],
              lerp: POINTER_FOLLOW_LERP,
              lights: {
                intensity: 200,
                colors: ["#f2f4f7", "#23d6ff", "#785cff", "#ff3d9a"],
              },
            },
          });
          appRef.current = app;

          const originalUpdate = app.tubes.update.bind(app.tubes);
          const visibleTarget = { ...app.tubes.target };
          const glideFrom = { ...visibleTarget };
          let pointerPresent = false;
          let glideStartedAt = 0;
          let glideDuration = 0;

          const beginGlide = (nextPointerPresent: boolean) => {
            if (nextPointerPresent === pointerPresent) return;

            pointerPresent = nextPointerPresent;
            glideFrom.x = visibleTarget.x;
            glideFrom.y = visibleTarget.y;
            glideFrom.z = visibleTarget.z;
            glideStartedAt = performance.now();
            glideDuration = nextPointerPresent ? POINTER_GLIDE_DURATION : LOOP_GLIDE_DURATION;
          };

          app.tubes.update = (time) => {
            const rawX = app.tubes.target.x;
            const rawY = app.tubes.target.y;
            const rawZ = app.tubes.target.z;
            const progress =
              glideDuration === 0
                ? 1
                : Math.min(1, (performance.now() - glideStartedAt) / glideDuration);
            const blend = smootherstep(progress);

            visibleTarget.x = glideFrom.x + (rawX - glideFrom.x) * blend;
            visibleTarget.y = glideFrom.y + (rawY - glideFrom.y) * blend;
            visibleTarget.z = glideFrom.z + (rawZ - glideFrom.z) * blend;
            app.tubes.target.x = visibleTarget.x;
            app.tubes.target.y = visibleTarget.y;
            app.tubes.target.z = visibleTarget.z;

            originalUpdate(time);

            if (progress === 1) glideDuration = 0;
          };

          const glideToLoop = () => beginGlide(false);
          const glideToPointer = () => beginGlide(true);
          setPointerPresence = (present) => (present ? glideToPointer() : glideToLoop());
          replayPointer = (pointer) => {
            document.body.dispatchEvent(
              new PointerEvent("pointermove", {
                bubbles: false,
                buttons: pointer.buttons,
                clientX: pointer.clientX,
                clientY: pointer.clientY,
                isPrimary: pointer.isPrimary,
                pointerId: pointer.pointerId,
                pointerType: pointer.pointerType,
              }),
            );
          };

          // A pointer may already be resting over the hero by the time the
          // async module finishes loading. Restore that position immediately.
          if (lastPointer && pointerHitsCanvas(lastPointer)) {
            replayPointer(lastPointer);
            glideToPointer();
          }

          removeTargetGlide = () => {
            app.tubes.update = originalUpdate;
            setPointerPresence = () => {};
            replayPointer = () => {};
          };
        })
        .catch((error: unknown) => {
          console.error("Failed to load the hero tubes cursor.", error);
        });
    }, 100);

    return () => {
      disposed = true;
      window.clearTimeout(initTimer);
      hero?.removeEventListener("click", randomize);
      document.body.removeEventListener("pointerleave", handleBodyPointerLeave, true);
      window.removeEventListener("pointermove", handlePointerActivity, true);
      window.removeEventListener("pointerover", handlePointerActivity, true);
      window.removeEventListener("pointerdown", handlePointerActivity, true);
      window.removeEventListener("blur", handleWindowBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      removeTargetGlide();
      appRef.current?.dispose?.();
      appRef.current = null;
    };
  }, [active]);

  return (
    <div aria-hidden="true" className={`tubes-cursor${active ? " is-active" : ""}`}>
      <canvas ref={canvasRef} />
    </div>
  );
}
